<script lang="ts" setup>
const emit = defineEmits<{
  (event: 'mouseenter'): void
  (event: 'mouseleave'): void
  (event: 'focus'): void
  (event: 'blur'): void
  (event: 'update:value', value: string | null): void
  (event: 'reset', value: string | null): void
}>()

const props = withDefaults(
  defineProps<{
    disabled?: boolean
    placeholder?: string
    value?: string | null | undefined
    default?: string | null
    id?: string
    multiline?: boolean
    rows?: number
    maxlength?: number | null
    isPassword?: boolean
  }>(),
  {
    disabled: false,
    placeholder: '',
    value: undefined,
    default: null,
    id: '',
    multiline: false,
    rows: 2,
    maxlength: null,
    isPassword: false,
  }
)

const internalValue = ref(typeof props.value !== 'undefined' ? props.value : null)
const value = computed({
  get: () => (typeof props.value !== 'undefined' ? props.value : internalValue.value),
  set: (v: string | null) => emit('update:value', (internalValue.value = v)),
})

const update = (v: string | null) => (value.value = v)
const reset = (v?: string | null) => emit('reset', (value.value = typeof v !== 'undefined' ? v : props.default))
const onInput = (ev: Event) => {
  if (ev && ev.target) value.value = (ev.target as HTMLInputElement).value
}

const { class: hoverClass, events: hoverEvents } = useHover(emit, props)
const { class: focusClass, events: focusEvents } = useFocus(emit, props)

defineExpose({ value, update, reset })
</script>

<template>
  <label :aria-disabled="props.disabled"
    :class="['tw-input', 'input-data-type--string', props.disabled && 'is--disabled', hoverClass, focusClass]"
    role="textbox" tabindex="-1" v-on="hoverEvents">
    <slot name="label" />
    <span v-if="$slots.before && $slots.before().length" class="tw-before">
      <slot :reset="reset" :update="update" :value="value" name="before" />
    </span>

    <component :is="multiline ? 'textarea' : 'input'" :id="props.id" :type="props.isPassword ? 'password' : 'text'"
      :disabled="props.disabled" :placeholder="props.placeholder" :value="value === null ? '' : value"
      :maxlength="props.maxlength" :rows="multiline ? props.rows : undefined" tabindex="0" @input="onInput"
      v-on="focusEvents" />

    <span v-if="$slots.after && $slots.after().length" class="tw-after">
      <slot :reset="reset" :update="update" :value="value" name="after" />
    </span>
  </label>
</template>

<style lang="scss">
@import "~/assets/styles/tw-variables.scss";
@import "~/assets/styles/tw-mixins.scss";

.tw-input {
  display: inline-flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: $tw-component--gap;
  padding: $tw-component--gap;
  border: 1px solid transparent;
  transition: color, border-color, background-color, text-decoration-color;
  transition-duration: 0.15s;
  transition-timing-function: ease-in-out;
  user-select: none;
  @include tw-default-box-variant('box');
  @include tw-color-set(inherit, transparent, $lightGray);

  &.is--hovered {
    @include tw-color-set(null, null, $primary);
  }

  &.is--disabled {
    @include tw-color-set($white, $gray, $gray);
  }

  input,
  textarea {
    flex: 1;
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
    border: none;
    outline: none;
    font-size: 1em;
    font-family: 'Sofia W01', sans-serif;
    @include tw-color-set(inherit, transparent);
  }
}
</style>
